import { ref, onMounted, onUnmounted } from 'vue'

export function useSmoothScroll() {
  const lenis = ref(null)
  const isScrolling = ref(false)

  // Custom smooth scroll implementation compatible with Node 16.14.0
  const createSmoothScroll = () => {
    let targetY = 0
    let currentY = 0
    let isRunning = false

    // Easing function for future use if needed
    // const easing = (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t))
    
    const lerp = (start, end, factor) => {
      return start + (end - start) * factor
    }

    const raf = () => {
      if (!isRunning) return

      currentY = lerp(currentY, targetY, 0.08)
      
      if (Math.abs(targetY - currentY) < 0.1) {
        currentY = targetY
        isRunning = false
        isScrolling.value = false
        return
      }

      window.scrollTo(0, currentY)
      requestAnimationFrame(raf)
    }

    const scrollTo = (target) => {
      targetY = target
      isRunning = true
      isScrolling.value = true
      requestAnimationFrame(raf)
    }

    // Handle wheel events for smooth scrolling
    const handleWheel = (e) => {
      e.preventDefault()
      
      const delta = e.deltaY
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop
      const maxScroll = document.documentElement.scrollHeight - window.innerHeight
      
      targetY = Math.max(0, Math.min(maxScroll, scrollTop + delta * 3))
      
      if (!isRunning) {
        isRunning = true
        isScrolling.value = true
        requestAnimationFrame(raf)
      }
    }

    let touchStartY = 0
    const handleTouchStart = (e) => {
      touchStartY = e.touches[0].clientY
    }

    const handleTouchMove = (e) => {
      if (isScrolling.value) {
        e.preventDefault()
      }
      
      const touchY = e.touches[0].clientY
      const delta = (touchStartY - touchY) * 2
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop
      const maxScroll = document.documentElement.scrollHeight - window.innerHeight
      
      targetY = Math.max(0, Math.min(maxScroll, scrollTop + delta))
      touchStartY = touchY
      
      if (!isRunning) {
        isRunning = true
        isScrolling.value = true
        requestAnimationFrame(raf)
      }
    }

    return {
      init: () => {
        currentY = window.pageYOffset || document.documentElement.scrollTop
        targetY = currentY

        window.addEventListener('wheel', handleWheel, { passive: false })
        window.addEventListener('touchstart', handleTouchStart, { passive: true })
        window.addEventListener('touchmove', handleTouchMove, { passive: false })
        
        document.documentElement.style.scrollBehavior = 'auto'
      },
      
      destroy: () => {
        window.removeEventListener('wheel', handleWheel)
        window.removeEventListener('touchstart', handleTouchStart)
        window.removeEventListener('touchmove', handleTouchMove)
        document.documentElement.style.scrollBehavior = 'smooth'
        isRunning = false
        isScrolling.value = false
      },
      
      scrollTo: (target) => {
        if (typeof target === 'number') {
          scrollTo(target)
        } else if (typeof target === 'string') {
          const element = document.querySelector(target)
          if (element) {
            const rect = element.getBoundingClientRect()
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop
            scrollTo(scrollTop + rect.top)
          }
        }
      },
      
      stop: () => {
        isRunning = false
        isScrolling.value = false
      }
    }
  }

  onMounted(() => {
    try {
      lenis.value = createSmoothScroll()
      lenis.value.init()
    } catch (error) {
      console.warn('Smooth scroll initialization failed:', error)
      document.documentElement.style.scrollBehavior = 'smooth'
    }
  })

  onUnmounted(() => {
    if (lenis.value) {
      lenis.value.destroy()
      lenis.value = null
    }
  })

  return { 
    lenis,
    isScrolling,
    scrollTo: (target) => lenis.value?.scrollTo(target),
    stop: () => lenis.value?.stop()
  }
}